import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Check, X } from "lucide-react";
import api from "@/lib/api";

const LETTERS = ["A", "B", "C", "D"];

export default function Quiz() {
  const params = useParams();
  const { pid, rid } = params;
  const sid = params.sid || "final";
  const { setCrumbs } = useOutletContext();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [path, setPath] = useState(null);
  const [index, setIndex] = useState(0);
  const [answered, setAnswered] = useState(null); // chosen choiceIndex
  const [reveal, setReveal] = useState(null); // { correct, correctIndex, explanation }
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null); // final assessment result
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get(`/quiz/${pid}/${rid}/${sid}`), api.get(`/catalog/${pid}/${rid}`)])
      .then(([q, p]) => {
        setMeta(q.data);
        setPath(p.data);
        setCrumbs([
          { label: p.data.product.name, to: `/${pid}` },
          { label: p.data.role.name, to: `/${pid}/${rid}` },
          { label: q.data.isFinal ? "Final assessment" : "Knowledge check", to: null },
        ]);
        window.scrollTo(0, 0);
      })
      .catch(() => navigate(`/${pid}/${rid}`, { replace: true }));
  }, [pid, rid, sid, navigate, setCrumbs]);

  useEffect(() => { load(); }, [load]);

  if (!meta || !path) return <div className="loading">Loading the questions…</div>;

  const count = meta.count;
  const question = meta.questions[index];

  const pick = async (choiceIndex) => {
    if (answered !== null) return;
    setAnswered(choiceIndex);
    const { data } = await api.post(`/quiz/${pid}/${rid}/${sid}/grade`, { questionIndex: index, choiceIndex });
    setReveal(data);
    if (data.correct) setCorrectCount((c) => c + 1);
  };

  const finishQuiz = async () => {
    setBusy(true);
    try {
      if (meta.isFinal) {
        const score = Math.round((correctCount / count) * 100);
        const { data } = await api.post(`/progress/${pid}/${rid}/assessment`, { score });
        setResult(data);
      } else {
        await api.post(`/progress/${pid}/${rid}/check`, { sectionId: sid, passed: true });
      }
      setFinished(true);
      window.scrollTo(0, 0);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (index + 1 >= count) return finishQuiz();
    setIndex((i) => i + 1);
    setAnswered(null);
    setReveal(null);
  };

  const retry = () => {
    setIndex(0); setAnswered(null); setReveal(null); setCorrectCount(0); setFinished(false); setResult(null);
    window.scrollTo(0, 0);
  };

  const sectionNextTarget = () => {
    const si = path.sections.findIndex((s) => s.id === sid);
    if (si >= 0 && si < path.sections.length - 1) {
      const nextSec = path.sections[si + 1];
      return `/${pid}/${rid}/${nextSec.id}/${nextSec.modules[0].id}`;
    }
    return `/${pid}/${rid}`;
  };

  const progressPct = Math.round(((index + (answered !== null ? 1 : 0)) / count) * 100);
  const isLast = index + 1 >= count;

  // ---- finished screens ----
  if (finished) {
    if (meta.isFinal && result) {
      const passed = result.passed;
      return (
        <section className="page" data-testid="quiz-result">
          <div className="quiz">
            <div className="quiz__card">
              <div className="result">
                <div className="eyebrow-accent">{meta.crumb}</div>
                <div className={`result__score ${passed ? "result__score--pass" : "result__score--fail"}`} data-testid="result-score">{result.score}%</div>
                <h1 style={{ fontSize: "var(--fs-card-title-lg)", fontWeight: "var(--fw-extrabold)", margin: "0 0 10px" }}>
                  {passed ? "You're certified." : "Not quite there."}
                </h1>
                <p className="page-sub" style={{ margin: "0 auto 24px", maxWidth: "460px" }}>
                  {passed
                    ? `You scored ${result.score}% against a ${result.passMark}% pass mark.`
                    : result.lockedUntil
                      ? `You scored ${result.score}%, below the ${result.passMark}% pass mark. You've used your retries — the assessment unlocks again in 24 hours.`
                      : `You scored ${result.score}%, below the ${result.passMark}% pass mark. You can try again.`}
                </p>
                <div className="quiz__foot" style={{ justifyContent: "center" }}>
                  {passed ? (
                    <button className="btn btn--dark" onClick={() => navigate(`/certificate/${pid}/${rid}`)} data-testid="see-certificate-btn">See your certificate</button>
                  ) : (
                    !result.lockedUntil && <button className="btn btn--dark" onClick={retry} data-testid="retry-btn">Try again</button>
                  )}
                  <button className="btn btn--outline" onClick={() => navigate(`/${pid}/${rid}`)} data-testid="back-to-path-btn">Back to the path</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }
    // section check complete
    return (
      <section className="page" data-testid="quiz-result">
        <div className="quiz">
          <div className="quiz__card">
            <div className="result">
              <div className="eyebrow-accent">{meta.crumb}</div>
              <div className="result__score result__score--pass" data-testid="result-score">{correctCount}/{count}</div>
              <h1 style={{ fontSize: "var(--fs-card-title-lg)", fontWeight: "var(--fw-extrabold)", margin: "0 0 10px" }}>Knowledge check complete.</h1>
              <p className="page-sub" style={{ margin: "0 auto 24px", maxWidth: "460px" }}>
                You got {correctCount} of {count} right. Your progress is saved.
              </p>
              <div className="quiz__foot" style={{ justifyContent: "center" }}>
                <button className="btn btn--dark" onClick={() => navigate(sectionNextTarget())} data-testid="continue-btn">Continue</button>
                <button className="btn btn--outline" onClick={() => navigate(`/${pid}/${rid}`)} data-testid="back-to-path-btn">Back to the path</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---- question screen ----
  return (
    <section className="page" data-testid="quiz">
      <div className="quiz">
        <div className="quiz__card">
          <div className="quiz__top">
            <span className="quiz__crumb">{meta.crumb}</span>
            <span className="quiz__count" data-testid="quiz-progress">Question {index + 1} of {count}</span>
          </div>
          <div className="quiz__progress"><div className="quiz__progress-fill" style={{ width: `${progressPct}%` }} /></div>
          <div className="quiz__body">
            <h1 className="quiz__question" data-testid="quiz-question">{question.prompt}</h1>
            <div className="quiz__options">
              {question.options.map((opt, i) => {
                let cls = "opt";
                let mark = null;
                if (reveal) {
                  if (i === reveal.correctIndex) { cls += " opt--correct"; mark = <Check size={18} strokeWidth={3} color="var(--success-ink)" />; }
                  else if (i === answered) { cls += " opt--wrong"; mark = <X size={18} strokeWidth={3} color="var(--error-ink)" />; }
                }
                return (
                  <button key={i} className={cls} onClick={() => pick(i)} disabled={answered !== null} data-testid={`quiz-option-${i}`}>
                    <span className="opt__letter">{LETTERS[i]}</span>
                    <span className="opt__label">{opt}</span>
                    {mark && <span className="opt__mark">{mark}</span>}
                  </button>
                );
              })}
            </div>

            {reveal && (
              <div className={`explain ${reveal.correct ? "explain--correct" : "explain--wrong"}`} data-testid="quiz-explanation">
                <span className="explain__label">{reveal.correct ? "Correct" : "Not quite"}</span>
                {reveal.explanation}
              </div>
            )}

            <div className="quiz__foot">
              <button className="link-accent" onClick={() => navigate(`/${pid}/${rid}`)} data-testid="quiz-leave-btn">Leave the check</button>
              {reveal && (
                <button className="btn btn--dark push" onClick={next} disabled={busy} data-testid="quiz-next-btn">
                  {isLast ? (meta.isFinal ? "See your result" : "Finish the check") : "Next question"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
